import { useState, useEffect } from 'react';
import { Card, Row, Col, DatePicker, Typography, Spin, Statistic, Alert } from 'antd';
import { Line, Column } from '@ant-design/plots';
import { Dayjs } from 'dayjs';
import apiClient from '@/utils/apiClient';
import dayjs from 'dayjs';
import { RiseOutlined, EditOutlined, CheckCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface TrendPoint {
	date: string;
	value: number;
}

interface NotesData {
	date: string;
	count: number;
	type: 'Created' | 'Edited';
}

const TicketsAnalytics = () => {
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>([
		dayjs().subtract(29, 'd'),
		dayjs(),
	]);

	const [dauData, setDauData] = useState<TrendPoint[]>([]);
	const [notesData, setNotesData] = useState<NotesData[]>([]);
	const [notInOpenRate, setNotInOpenRate] = useState(0);
	const [completionRate, setCompletionRate] = useState(0);
	const [totalDau, setTotalDau] = useState<number>(0);
	const [avgSessionDuration, setAvgSessionDuration] = useState(0);
	const formatDuration = (seconds: number) => {
		if (isNaN(seconds) || seconds < 0) {
			return '0:00';
		}
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = Math.round(seconds % 60);
		return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
	};

	useEffect(() => {
		const fetchAnalyticsData = async () => {
			if (!dateRange || !dateRange[0] || !dateRange[1]) {
				return;
			}
			setLoading(true);
			setError(null);
			try {
				const response = await apiClient.request({
					endPoint: '/api/admin/analytics/tickets',
					method: 'POST',
					body: {
						startDate: dateRange[0].format('YYYY-MM-DD'),
						endDate: dateRange[1].format('YYYY-MM-DD'),
					},
					clientType: 'ADMIN',
				});

				if (response.success && response.data) {
					setDauData(
						response.data.dailyActiveUsers.data.map((count: number, index: number) => ({
							date: dayjs(response.data.dailyActiveUsers.days[index]).format('MMM D'),
							value: count,
						})),
					);
					setTotalDau(response.data.totalActiveUsers || 0);

					const formattedNotes: NotesData[] = [];
					response.data.notesCreatedVsEdited.forEach((series: any) => {
						const type = series?.label?.includes('created') ? 'Created' : 'Edited';
						series.data.forEach((count: number, index: number) => {
							formattedNotes.push({
								date: dayjs(series.days[index]).format('MMM D'),
								count,
								type,
							});
						});
					});
					setNotesData(formattedNotes);

					setCompletionRate(response.data.completionRate || 0);
					setNotInOpenRate(response.data.notInOpenRate || 0);
					setAvgSessionDuration(response.data.avgTimeOnPage || 0);
				}
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to load analytics data.');
			} finally {
				setLoading(false);
			}
		};
		fetchAnalyticsData();
	}, [dateRange]);

	const totalNotesCreated = notesData
		.filter((n) => n.type === 'Created')
		.reduce((sum, item) => sum + item.count, 0);

	return (
		<div style={{ padding: '24px' }}>
			<Title level={2}>Tickets Engagement</Title>
			<Card style={{ marginBottom: '24px' }}>
				<Text strong>Filter by Date Range:</Text>
				<br />
				<RangePicker
					value={dateRange}
					onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs] | null)}
				/>
			</Card>

			{error && <Alert message="Error" description={error} type="error" showIcon />}

			<Spin spinning={loading}>
				<Row gutter={[24, 24]}>
					<Col xs={24} md={8}>
						<Card>
							<Statistic title="Total Active Users" value={totalDau} prefix={<RiseOutlined />} />
						</Card>
					</Col>
					<Col xs={24} md={8}>
						<Card>
							<Statistic
								title="Avg. Session Time"
								value={formatDuration(avgSessionDuration)}
								suffix="min"
							/>
						</Card>
					</Col>
					<Col xs={24} md={8}>
						<Card>
							<Statistic
								title="Total Notes Created"
								value={totalNotesCreated}
								prefix={<EditOutlined />}
							/>
						</Card>
					</Col>
					<Col xs={24} md={8}>
						<Card>
							<Statistic
								title="% Tickets Not in 'Open' Stage"
								value={`${notInOpenRate.toFixed(1)}%`}
								prefix={<CheckCircleOutlined />}
							/>
						</Card>
					</Col>
					<Col xs={24} md={8}>
						<Card>
							<Statistic
								title="% of Stage Changes to 'Completed'"
								value={`${completionRate.toFixed(1)}%`}
								prefix={<CheckCircleOutlined />}
							/>
						</Card>
					</Col>

					<Col xs={24}>
						<Card title="Daily Active Users - Tickets">
							<div style={{ height: 300 }}>
								<Line data={dauData} xField="date" yField="value" />
							</div>
						</Card>
					</Col>
					<Col xs={24}>
						<Card title="Notes Created vs. Edited (Daily)">
							<div style={{ height: 300 }}>
								<Column
									data={notesData}
									xField="date"
									yField="count"
									seriesField="type"
									colorField="type"
									isStack={true}
									color={['#1890ff', '#52c41a', '#ff7875']}
								/>
							</div>
						</Card>
					</Col>
				</Row>
			</Spin>
		</div>
	);
};

export default TicketsAnalytics;
